import { useState } from 'react'
import { useSnackbar } from 'notistack'
import { ArrowLeft, RefreshCw, Archive, RotateCcw } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Button } from 'reactstrap'
import {
  setItemCount,
  setOrders,
  stepOrder,
} from 'features/private-views/purchase-commodity/purchaseCommoditySlice'
import { useAbilityAccess } from 'hooks/useAbilityAccess'
import {
  BUSINESS_TRANSACTION_PURCHASE,
  MAKE,
  MANAGE,
} from 'utils/subjectActions'
import moment from 'moment-timezone'
import { useArchiveTransactionMutation } from '../../dashboardApiSlice'
import ModalArchiveTransaction from '../../TableDashboard/ModalArchiveTransaction'

const HeaderSection = ({ transaction, isBuyer, isCreator, refetch }) => {
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { profile } = useSelector((s) => s.dashboard)
  const [archiveTrx, { isLoading: isLoadingArchive }] =
    useArchiveTransactionMutation()
  const [isModalArchiveOpen, setIsModalArchiveOpen] = useState(false)
  const [archiveErrMsg, setArchiveErrMsg] = useState(null)

  const isArchived = isBuyer
    ? Boolean(
        Number(transaction?.buyerArchived) === 1 ||
          transaction?.buyerArchived === true,
      )
    : Boolean(
        Number(transaction?.sellerArchived) === 1 ||
          transaction?.sellerArchived === true,
      )

  const canBeArchived =
    !isArchived &&
    String(transaction?.status || '').toLowerCase() === 'selesai'

  const handleConfirmArchive = async () => {
    const alltradeId = transaction?.alltradeId || transaction?.allTradeId
    const escrowId =
      transaction?.escrowpaymentId || transaction?.escrowPaymentId
    const shortcutId =
      transaction?.tradeshortcutId || transaction?.tradeShortcutId

    const payload = {
      archive: isArchived ? 0 : 1,
    }

    if (alltradeId) {
      payload.alltradeIds = [Number(alltradeId)]
    } else if (escrowId) {
      payload.escrowpaymentIds = [Number(escrowId)]
    } else if (shortcutId) {
      payload.tradeshortcutIds = [Number(shortcutId)]
    } else if (transaction?.id) {
      payload.alltradeIds = [Number(transaction.id)]
    }

    try {
      await archiveTrx(payload).unwrap()
      enqueueSnackbar(
        isArchived
          ? 'Transaksi berhasil dikembalikan dari arsip'
          : 'Transaksi berhasil diarsipkan',
        { variant: 'success', autoHideDuration: 3000 },
      )
      setIsModalArchiveOpen(false)
      refetch?.()
    } catch (err) {
      const msg =
        err?.data?.message ||
        err?.data?.meta?.message ||
        'Gagal memproses transaksi. Silakan coba lagi.'
      setArchiveErrMsg(msg)
    }
  }

  const makePurchase = useAbilityAccess({
    action: MAKE,
    subject: BUSINESS_TRANSACTION_PURCHASE,
  })
  const managePurchase = useAbilityAccess({
    action: MANAGE,
    subject: BUSINESS_TRANSACTION_PURCHASE,
  })

  return (
    <div className="row align-items-center py-3">
      <div className="col-12 col-md-4 d-flex align-items-center mb-2 mb-md-0">
        <ArrowLeft
          size={20}
          className="me-2"
          style={{ cursor: 'pointer' }}
          onClick={() => navigate(-1)}
        />
        <h6 className="fw-bold mb-0">
          Detail {isBuyer ? 'Pembelian' : 'Penjualan'}
        </h6>
      </div>

      <div className="col-12 col-md-8 d-flex justify-content-md-end gap-2 flex-column flex-md-row">
        {isCreator && (
          <Button
            color="primary"
            className="d-flex align-items-center justify-content-center w-md-auto mb-2 mb-md-0 gap-2"
            onClick={() => {
              try {
                const sourceItems = transaction?.itemDetail || []

                const mappedItems = (sourceItems || []).map((d, idx) => {
                  const rawAmount = d?.amount ?? d?.newAmount ?? 0
                  const rawPrice = d?.price ?? d?.newPrice ?? 0

                  const parseNumber = (v) => {
                    if (v === null || v === undefined || v === '') return 0
                    if (typeof v === 'number') return v
                    const s = String(v).trim()
                    const cleaned = s.replace(/\./g, '').replace(/,/g, '.')
                    return Number(cleaned) || 0
                  }

                  const unitVal =
                    d?.unit?.value ??
                    d?.unit ??
                    d?.item?.unit?.value ??
                    d?.item?.unit ??
                    ''
                  const unitStr = String(unitVal).toLowerCase()

                  const numericRawAmount = parseNumber(rawAmount)
                  let amount = numericRawAmount

                  if (
                    unitStr === 'kg' &&
                    Number.isInteger(numericRawAmount) &&
                    numericRawAmount >= 100 &&
                    numericRawAmount % 10 !== 0
                  ) {
                    amount = numericRawAmount / 100
                  }
                  const price = parseNumber(rawPrice)
                  const itemId =
                    d?.itemId ?? d?.id ?? d?.item?.id ?? d?.item?.value ?? null
                  return {
                    orderItemId: idx + 1,
                    SKU: d?.SKU ?? d?.sku ?? '',
                    itemId: itemId,
                    id: itemId,
                    inventoryId:
                      d?.inventoryId ??
                      d?.itemDetail?.inventoryId ??
                      d?.item?.inventoryId ??
                      null,
                    itemDetail: d,
                    amount: amount,
                    imageUrl: d?.imageUrl ?? d?.image ?? '',
                    nameItem:
                      d?.nameItem ??
                      d?.name ??
                      d?.item?.name ??
                      d?.item?.label ??
                      d?.item?.value ??
                      '',
                    price: price,
                    totalPrice: d?.totalPrice ?? amount * price,
                    unit: {
                      label: d?.unit ?? d?.item?.unit ?? 'Kg',
                      value: d?.unit ?? d?.item?.unit ?? 'Kg',
                    },
                  }
                })

                const orders = {
                  isLoading: false,
                  step: 0,
                  userId: transaction?.buyerDetail?.userId ?? null,
                  item:
                    mappedItems.length > 0
                      ? mappedItems
                      : [
                          {
                            orderItemId: 1,
                            SKU: '',
                            itemId: null,
                            amount: '',
                            imageUrl: '',
                            nameItem: '',
                            price: '',
                            totalPrice: '',
                            unit: { label: 'Kg', value: 'Kg' },
                          },
                        ],
                  order: {
                    type:
                      transaction?.orderDetail?.type ??
                      transaction?.type ??
                      null,
                    notes:
                      transaction?.orderDetail?.notes ??
                      transaction?.notes ??
                      '',
                    transaction_number: '',
                  },
                  buyer: transaction?.buyerDetail ?? null,
                  seller: transaction?.sellerDetail ?? null,
                  sellerPhoneNumber:
                    transaction?.sellerDetail?.phoneNumber ?? null,
                  trxDate: moment(new Date()).format('YYYY-MM-DD'),
                  project: transaction?.project
                    ? {
                        ...transaction.project,
                        value: transaction?.project?.id,
                        label:
                          transaction?.project?.label ||
                          transaction?.project?.projectName ||
                          transaction?.project?.title ||
                          transaction.projectName,
                      }
                    : transaction?.projectName
                      ? {
                          label: transaction.projectName,
                          value: transaction.projectName,
                        }
                      : null,
                }
                // indicate which items are inventory selections so the form
                // can render the correct UI (Inventori Anda vs Komoditas Umum)
                orders.isInventory = (orders.item || []).map(
                  (it) => !!(it?.inventoryId || it?.itemDetail?.inventoryId),
                )

                const addItemCount = mappedItems.map((m) => m.orderItemId)

                const trxType =
                  transaction?.orderDetail?.type ??
                  transaction?.type ??
                  transaction?.order?.type
                const route =
                  trxType === 'SELL'
                    ? '/sell-commodity'
                    : makePurchase && !managePurchase
                      ? '/purchase-commodity'
                      : '/transaksi-pembelian-tambah-komoditas'

                // Ensure top-level userId matches the actor creating the order:
                // - For SELL: userId should be the seller (current user's id)
                // - For PURCHASE: userId should be the buyer
                if (trxType === 'SELL') {
                  orders.userId = profile?.userId
                  // For Sell:
                  // buyer = buyer from transaction (the customer)
                  // seller = me (profile)
                  orders.seller = {
                    ...profile,
                    ...(profile?.ownerData || {}),
                  }
                  orders.buyer = {
                    ...transaction?.buyerDetail,
                    profileStatus:
                      transaction?.buyerDetail?.profileStatus ??
                      transaction?.buyer?.profileStatus ??
                      'UNVERIFIED',
                  }
                } else {
                  orders.userId = Number(
                    transaction?.buyerDetail?.userId ??
                      transaction?.buyer?.userId ??
                      transaction?.userId ??
                      NaN,
                  )
                }

                if (makePurchase && !managePurchase) {
                  dispatch(setOrders(orders))
                  dispatch(setItemCount(addItemCount))
                  dispatch(
                    stepOrder({
                      step: 1,
                      type: 'BUY',
                    }),
                  )
                }

                const phoneNumberToFetch =
                  trxType === 'SELL'
                    ? (transaction?.buyerDetail?.phoneNumber ?? null)
                    : (transaction?.sellerDetail?.phoneNumber ?? null)

                navigate(route, {
                  state: {
                    prefillOrders: orders,
                    prefillAddItemCount: addItemCount,
                    shouldFetchSeller: true,
                    sellerPhoneNumber: phoneNumberToFetch,
                  },
                })
              } catch (e) {
                console.warn('repeat order failed', e)
              }
            }}
          >
            <RefreshCw size={16} className="me-1" />
            Ulangi Pesanan
          </Button>
        )}

        {isArchived ? (
          <Button
            color="secondary"
            outline
            className="d-flex align-items-center justify-content-center w-md-auto mb-2 mb-md-0 gap-2"
            onClick={() => {
              setArchiveErrMsg(null)
              setIsModalArchiveOpen(true)
            }}
            data-testid="detail-transaction-unarchive-button"
          >
            <RotateCcw size={16} />
            <span>Keluarkan dari Arsip</span>
          </Button>
        ) : canBeArchived ? (
          <Button
            color="secondary"
            outline
            className="d-flex align-items-center justify-content-center w-md-auto mb-2 mb-md-0 gap-2"
            onClick={() => {
              setArchiveErrMsg(null)
              setIsModalArchiveOpen(true)
            }}
            data-testid="detail-transaction-archive-button"
          >
            <Archive size={16} />
            <span>Arsipkan</span>
          </Button>
        ) : null}
      </div>

      <ModalArchiveTransaction
        isOpen={isModalArchiveOpen}
        toggle={() => setIsModalArchiveOpen((prev) => !prev)}
        handleConfirm={handleConfirmArchive}
        isLoading={isLoadingArchive}
        errMsg={archiveErrMsg}
        isArchive={!isArchived}
        count={1}
      />
    </div>
  )
}

export default HeaderSection
